import { GaugeData, GaugeConfig } from "@/lib/types/gauge";
import { dashboard_element, display_config } from "@/lib/types";
import { calculateMarkerAngle, percentileFor } from "@/lib/utils/gauge-calculations";

/**
 * How far the needle moved from the lag marker, in degrees, or null when
 * either can't be placed (no prior value, say).
 */
const needleMove = (gaugeData: GaugeData): number | null => {
  const current = percentileFor(gaugeData, "current");
  const previous = percentileFor(gaugeData, "lag");
  if (current === null || previous === null) return null;
  return Math.abs(calculateMarkerAngle(current) - calculateMarkerAngle(previous));
};

function isDisplayConfig(e: dashboard_element): e is display_config {
  return typeof e === "object" && "display_id" in e && !("headerTitle" in e);
}

function getDisplayId(e: dashboard_element): string {
  if (typeof e === "string") return e;
  if (isDisplayConfig(e)) return e.display_id;
  return "";
}

export enum SortType {
  ALPHABETICAL = "alphabetical",
  NEXT_RELEASE = "next_release",
  LATEST_RELEASE = "latest_release",
  DATE = "date",
  DEFAULT = "default",
  NONE = "none",
  EXTREME_CHANGE = "extreme_change",
}

export enum SortDirection {
  ASC = "asc",
  DESC = "desc",
}

export interface SortConfig {
  type: SortType;
  direction: SortDirection;
  label: string;
  description?: string;
}

export const SORT_CONFIGS: Record<SortType, SortConfig> = {
  [SortType.ALPHABETICAL]: {
    type: SortType.ALPHABETICAL,
    direction: SortDirection.ASC,
    label: "Alphabetical",
    description: "Sort alphabetically",
  },
  [SortType.NEXT_RELEASE]: {
    type: SortType.NEXT_RELEASE,
    direction: SortDirection.DESC,
    label: "Next Release",
    description: "Sort by next release date",
  },
  [SortType.LATEST_RELEASE]: {
    type: SortType.LATEST_RELEASE,
    direction: SortDirection.DESC,
    label: "Latest Release",
    description: "Sort by latest release date",
  },
  [SortType.DATE]: {
    type: SortType.DATE,
    direction: SortDirection.DESC,
    label: "Date",
    description: "Sort by date",
  },
  [SortType.DEFAULT]: {
    type: SortType.DEFAULT,
    direction: SortDirection.ASC,
    label: "Default",
    description: "Use default sorting order",
  },
  [SortType.NONE]: {
    type: SortType.NONE,
    direction: SortDirection.ASC,
    label: "No Sorting",
    description: "No sorting applied",
  },
  [SortType.EXTREME_CHANGE]: {
    type: SortType.EXTREME_CHANGE,
    direction: SortDirection.DESC,
    label: "Most Extreme Change",
    description:
      "Sorting by biggest change in gauge position (angle) compared to the previous period",
  },
};

export function sortGaugeElements(
  elements: dashboard_element[],
  gaugeData: Record<string, GaugeData>,
  gaugeConfig: Record<string, GaugeConfig>,
  sortType: SortType,
  direction: SortDirection = SortDirection.ASC,
): dashboard_element[] {
  const regularGauges = elements.filter(
    (e) => typeof e === "string" || isDisplayConfig(e),
  );
  const guideTiles = elements.filter(
    (e) => typeof e === "object" && "headerTitle" in e,
  );

  const sortedGauges = [...regularGauges].sort((a, b) => {
    const idA = getDisplayId(a);
    const idB = getDisplayId(b);
    const gaugeA = gaugeData[idA];
    const gaugeB = gaugeData[idB];
    const configA = gaugeConfig[idA];
    const configB = gaugeConfig[idB];

    if (!gaugeA || !gaugeB) return 0;

    let comparison = 0;

    switch (sortType) {
      case SortType.ALPHABETICAL:
        comparison = (configA?.display_name ?? "").localeCompare(
          configB?.display_name ?? "",
        );
        break;

      case SortType.NEXT_RELEASE:
        const nextReleaseA = new Date(configA?.next_release || 0).getTime();
        const nextReleaseB = new Date(configB?.next_release || 0).getTime();
        comparison = nextReleaseA - nextReleaseB;
        // For next release, DESC should show earliest dates first (most urgent)
        return direction === SortDirection.DESC ? comparison : -comparison;

      case SortType.LATEST_RELEASE:
        const latestReleaseA = new Date(configA?.last_updated || 0).getTime();
        const latestReleaseB = new Date(configB?.last_updated || 0).getTime();
        comparison = latestReleaseB - latestReleaseA;
        break;

      case SortType.DATE:
        const dateA = new Date(gaugeA.date || 0).getTime();
        const dateB = new Date(gaugeB.date || 0).getTime();
        comparison = dateA - dateB;
        break;

      case SortType.EXTREME_CHANGE:
        // Calculate gauge angles for current and previous values
        // This sorts by the extremity of change in gauge position, not raw value differences
        // This approach keeps all gauges grouped together and sorts by how much the needle moved
        // Raw data series (like GDP) that are always growing will show at the extreme,
        // but users can toggle display format to see changes in context they prefer
        const angleDiffA = needleMove(gaugeA);
        const angleDiffB = needleMove(gaugeB);

        // A gauge whose move is unknown goes last in either direction, rather
        // than ranking as a move it didn't make.
        if (angleDiffA === null || angleDiffB === null) {
          if (angleDiffA === angleDiffB) return 0;
          return angleDiffA === null ? 1 : -1;
        }

        comparison = angleDiffA - angleDiffB;
        break;

      default:
        return 0;
    }

    // Apply direction
    return direction === SortDirection.DESC ? -comparison : comparison;
  });
  return [...sortedGauges, ...guideTiles];
}
